import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Header, HeaderBackButton, HeaderCopyButton, HeaderUserMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import { AiBrain02Icon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { MainScrollArea, MainHeader } from '@/ui/components/main';
import { Button } from '@/ui/components/button';
import { H1 } from '@/ui/components/heading';
import { KnowledgeBaseList } from '@/features/knowledge-base/components/knowledge-base-list';
import {
  createKnowledgeBaseOpenAtom,
  createKnowledgeBaseTypeAtom,
} from '@/features/knowledge-base/store';
import { useAtom, useSetAtom } from 'jotai';
import { CreateKnowledgeBaseDialog } from '@/features/knowledge-base/components/create-knowledge-base';
import { EditKnowledgeBase } from '@/features/knowledge-base/components/edit-knowledge-base';
import { organizationKnowledgeBasesQuery } from '@/features/knowledge-base/queries';
import { KnowledgeBaseType } from '@/features/knowledge-base/constants';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import { TenderFeedbackList } from '@/features/tender-feedbacks/components/tender-feedback-list';
import { useNavigate } from '@tanstack/react-router';
import z from 'zod';

const TABS = ['internal', 'internal_handbook', 'tender_feedbacks', 'company_documents'] as const;

export const Route = createFileRoute('/_authed/settings/knowledge-base/')({
  component: RouteComponent,
  validateSearch: z.object({
    tab: z.enum(TABS).default('internal').catch('internal'),
  }),
  loader: async ({ context }) => {
    await context.queryClient.query({
      ...organizationKnowledgeBasesQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const [createOpen, setCreateOpen] = useAtom(createKnowledgeBaseOpenAtom);
  const setCreateType = useSetAtom(createKnowledgeBaseTypeAtom);
  const search = Route.useSearch();
  const navigate = useNavigate();

  return (
    <>
      <CreateKnowledgeBaseDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditKnowledgeBase />
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={AiBrain02Icon} />
            {t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <div className="flex flex-col gap-1">
            <H1>{t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}</H1>
            <p className="text-muted text-sm">
              {t({
                en: "Your company's reference material. Volve draws on this across every project.",
                nb: 'Referansemateriale for ditt selskap. Volve trekker på dette på tvers av alle prosjekter.',
              })}
            </p>
          </div>
          <Button
            variant="secondary"
            className="max-md:w-full"
            kind="with-icon"
            onClick={() => {
              if (search.tab === 'tender_feedbacks')
                return navigate({ to: '/settings/knowledge-base/new-feedback' });
              if (search.tab === 'internal_handbook')
                setCreateType(KnowledgeBaseType.INTERNAL_HANDBOOK);
              if (search.tab === 'company_documents')
                setCreateType(KnowledgeBaseType.COMPANY_DOCUMENT);
              setCreateOpen(true);
            }}
          >
            <Icon icon={PlusSignIcon} />
            {search.tab === 'internal_handbook'
              ? t({ en: 'Add guide or reference', nb: 'Legg til veileder eller referanse' })
              : search.tab === 'tender_feedbacks'
                ? t({ en: 'Add past tender', nb: 'Legg til tidligere anbud' })
                : search.tab === 'company_documents'
                  ? t({ en: 'Add company document', nb: 'Legg til selskapsdokument' })
                  : t({ en: 'Add standard', nb: 'Legg til standard' })}
          </Button>
        </MainHeader>
        <Tabs
          value={search.tab}
          onValueChange={(value) =>
            navigate({ to: '.', replace: true, search: (prev) => ({ ...prev, tab: value }) })
          }
          className="flex flex-col grow min-h-0"
        >
          <TabsList className={'px-x'}>
            <TabsTab value={TABS[0]}>{t({ en: 'Standards', nb: 'Standarder' })}</TabsTab>
            <TabsTab value={TABS[1]}>
              {t({ en: 'Guides & references', nb: 'Veiledere og referanser' })}
            </TabsTab>
            <TabsTab value={TABS[2]}>{t({ en: 'Past tenders', nb: 'Tidligere anbud' })}</TabsTab>
            <TabsTab value={TABS[3]}>
              {t({ en: 'Company documents', nb: 'Selskapsdokumenter' })}
            </TabsTab>
          </TabsList>
          <TabsPanel value={TABS[0]}>
            <p className="text-muted text-sm px-x py-3 border-b">
              {t({
                en: 'The contract standards, technical standards and specification systems for your project portfolio. Applied across every project where relevant.',
                nb: 'Kontraktsstandardene, de tekniske standardene og beskrivelsessystemene for prosjektporteføljen din. Brukes på tvers av alle prosjekter der det er relevant.',
              })}
            </p>
            <KnowledgeBaseList kind="standards" />
          </TabsPanel>
          <TabsPanel value={TABS[1]}>
            <p className="text-muted text-sm px-x py-3 border-b">
              {t({
                en: 'Your approaches, methods and guidance, grouped by area. Volve draws on them where a project matches.',
                nb: 'Dine tilnærminger, metoder og veiledning, gruppert etter område. Volve trekker på dem der et prosjekt passer.',
              })}
            </p>
            <KnowledgeBaseList kind="guides" />
          </TabsPanel>
          <TabsPanel value={TABS[2]}>
            <p className="text-muted text-sm px-x py-3 border-b">
              {t({
                en: "Tenders you've delivered, with the RFP, submitted bid, and client or internal debriefs kept together, so you can reuse what worked and sharpen future bids.",
                nb: 'Anbud du har levert, med konkurransegrunnlag, innsendt tilbud og tilbakemeldinger fra kunde eller interne evalueringer samlet, slik at du kan gjenbruke det som fungerte og styrke fremtidige tilbud.',
              })}
            </p>
            <TenderFeedbackList />
          </TabsPanel>
          <TabsPanel value={TABS[3]}>
            <p className="text-muted text-sm px-x py-3 border-b">
              {t({
                en: 'Certificates, company information, policies and submission templates. Volve flags anything expiring.',
                nb: 'Sertifikater, selskapsinformasjon, retningslinjer og innsendingsmaler. Volve markerer det som snart utløper.',
              })}
            </p>
            <KnowledgeBaseList kind="companyDocuments" />
          </TabsPanel>
        </Tabs>
      </MainScrollArea>
    </>
  );
}
